import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Buildings, MapPin, Trophy, ArrowLeft } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrganizerProfile = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/organizer-profile/${id}`)
      .then(r => setData(r.data))
      .catch(e => logError('Failed to load organizer profile:', e))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center text-gray-500">Loading...</div>;
  if (!data) return <div className="min-h-screen pt-24 flex items-center justify-center text-gray-500">Organizer not found</div>;

  const { organizer, tournaments, tournaments_count } = data;

  return (
    <>
      <SEO title={`${organizer.name} | Tournament Organizer`} description={`Tournaments hosted by ${organizer.name} on Beyond Ball India.`} path={`/organizer-profile/${id}`} />

      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/tournaments" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#F97316] mb-6" data-testid="back-link">
            <ArrowLeft size={16} weight="bold" /> Back
          </Link>

          <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-8 animate-fade-up">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white flex-shrink-0">
                <Buildings size={28} weight="bold" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-[#F97316] uppercase tracking-wider mb-1">Organizer</div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-1" data-testid="organizer-name">{organizer.name}</h1>
                {organizer.city && <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3"><MapPin size={14} weight="bold" /> {organizer.city}</div>}
                {organizer.bio && <p className="text-gray-700 mb-3 leading-relaxed">{organizer.bio}</p>}
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Trophy size={14} weight="bold" className="text-[#F97316]" />
                  <span><strong className="text-gray-900">{tournaments_count}</strong> tournament{tournaments_count !== 1 ? 's' : ''} hosted</span>
                </div>
              </div>
            </div>
          </div>

          <h2 className="font-bold text-gray-900 text-lg mb-4 animate-fade-up delay-100">Tournaments by {organizer.name}</h2>

          {tournaments.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
              <Trophy size={48} weight="duotone" className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No approved tournaments yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-up delay-200">
              {tournaments.map((t, i) => (
                <Link key={t.tournament_id} to={`/tournaments/${t.tournament_id}`} className="card-lift bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid={`tournament-${i}`}>
                  <div className="aspect-[16/10] bg-gradient-to-br from-orange-100 to-orange-50">
                    {t.banner_url ? <img src={t.banner_url} alt={t.name} className="w-full h-full object-cover" style={{ objectPosition: `${t.banner_focus_x ?? 50}% ${t.banner_focus_y ?? 50}%` }} loading="lazy" /> :
                      <div className="w-full h-full flex items-center justify-center"><span className="text-5xl font-bold text-orange-200">{t.format}</span></div>}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">{t.name}</h3>
                    <div className="text-xs text-gray-500">{t.city}, {t.state}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrganizerProfile;
